import { useMemo } from 'react';
import { Select } from 'antd';
import { COUNTRIES } from '@shared/lib/countries';

interface CountryMultiSelectProps {
  value: string[] | null | undefined;
  onChange: (codes: string[]) => void;
  id?: string;
  invalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
  'aria-describedby'?: string;
}

/**
 * Multi-country picker (ISO-3166-1 alpha-2). Uses antd's Select directly —
 * the shadcn-style `Select` wrapper in shared/ui is single-value only.
 */
export function CountryMultiSelect({
  value,
  onChange,
  id,
  invalid,
  placeholder,
  disabled,
  ...aria
}: CountryMultiSelectProps) {
  const options = useMemo(
    () => COUNTRIES.map((c) => ({ value: c.code, label: `${c.nameEn} (${c.code})` })),
    [],
  );

  return (
    <Select
      id={id}
      mode="multiple"
      allowClear
      showSearch
      disabled={disabled}
      status={invalid ? 'error' : undefined}
      aria-describedby={aria['aria-describedby']}
      style={{ width: '100%' }}
      placeholder={placeholder}
      value={value ?? []}
      // Backend stores these as a Set and returns them alphabetically. Sorting
      // here keeps the form's order identical to what comes back, so removing
      // and re-adding a country doesn't leave `isDirty` stuck on.
      onChange={(codes: string[]) => onChange([...codes].sort())}
      options={options}
      // Search by country name or code, not by the rendered label only.
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      maxTagCount="responsive"
    />
  );
}
